import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, Search, SlidersHorizontal, X } from 'lucide-react';

/** Below Tailwind's `sm`: the panel is a sheet from the top, not a popover. */
const PHONE_QUERY = '(max-width: 639px)';

export const usePhone = () => {
  const [phone, setPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches,
  );
  useEffect(() => {
    const query = window.matchMedia(PHONE_QUERY);
    const update = () => setPhone(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return phone;
};

export interface FilterOption {
  value: string;
  label: string;
}

type OptionListProps = { options: FilterOption[]; disabled?: boolean } & (
  | { multiple?: false; value: string; onChange: (value: string) => void }
  | { multiple: true; value: string[]; onChange: (value: string[]) => void; allLabel: string }
);

/**
 * A filter's choices as rows to tap - a radio list, or a checklist with
 * `multiple` - instead of a dropdown to open. Long lists get a search box.
 */
export const OptionList = (props: OptionListProps) => {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const shown = q ? props.options.filter((o) => o.label.toLowerCase().includes(q)) : props.options;
  const isOn = (v: string) => (props.multiple ? props.value.includes(v) : props.value === v);
  const toggle = (v: string) => {
    if (props.multiple) props.onChange(isOn(v) ? props.value.filter((x) => x !== v) : [...props.value, v]);
    else props.onChange(v);
  };

  const row = (key: string, label: string, on: boolean, onClick: () => void) => (
    <button
      key={key}
      type="button"
      role={props.multiple ? 'checkbox' : 'radio'}
      aria-checked={on}
      disabled={props.disabled}
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors disabled:cursor-not-allowed ${
        on ? 'bg-indigo-50 font-semibold text-indigo-900' : 'text-slate-700 hover:bg-slate-50'
      }`}
    >
      {props.multiple ? (
        <span
          className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border-2 ${
            on ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300 bg-white'
          }`}
        >
          {on && <Check size={12} strokeWidth={3} />}
        </span>
      ) : (
        <span
          className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 bg-white ${
            on ? 'border-indigo-600' : 'border-slate-300'
          }`}
        >
          {on && <span className="h-2 w-2 rounded-full bg-indigo-600" />}
        </span>
      )}
      <span className="min-w-0 break-words">{label}</span>
    </button>
  );

  return (
    <div className={props.disabled ? 'opacity-50' : ''}>
      {props.options.length > 8 && (
        <div className="relative mb-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            disabled={props.disabled}
            className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15"
          />
        </div>
      )}
      <div role={props.multiple ? 'group' : 'radiogroup'} className="space-y-0.5">
        {props.multiple && !q && row('__all__', props.allLabel, props.value.length === 0, () => props.onChange([]))}
        {shown.map((o) => row(o.value, o.label, isOn(o.value), () => toggle(o.value)))}
        {shown.length === 0 && <p className="px-3 py-4 text-sm text-slate-400">No matches</p>}
      </div>
    </div>
  );
};

export interface FilterSection {
  id: string;
  /** Name in the left column. */
  label: string;
  /** What the draft has picked, under the name; unset when at its default. */
  summary?: string;
  /** One line under the heading on the right. */
  hint?: string;
  content: ReactNode;
}

interface Props {
  open: boolean;
  /** Called with true from the trigger, false from close / outside click / Escape. */
  onOpenChange: (open: boolean) => void;
  /** Filters narrowing the grid right now (applied, not the draft). */
  activeCount: number;
  sections: FilterSection[];
  /** Clear / Show results, pinned at the bottom. */
  footer: ReactNode;
  /**
   * Phone only: a control in the header beside the close button (Apply), so
   * it is in reach at the top of the screen as well as at the bottom.
   */
  headerAction?: ReactNode;
  title?: string;
}

/**
 * A grid's filters behind one button, laid out the way Amazon does it: the
 * filter names down the left, the picked one's choices on the right as rows
 * to tap, and the buttons along the bottom.
 *
 * Desktop: a "Filters" button with a count of what is applied, opening a
 * popover under it. Phone: an icon beside the search, opening the same panel
 * as a sheet over the top 75% of the screen.
 *
 * Portalled to <body> with fixed positioning, so the grid card's overflow
 * clipping cannot cut it off.
 */
const FilterPanel = ({ open, onOpenChange, activeCount, sections, footer, headerAction, title = 'Filters' }: Props) => {
  const phone = usePhone();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const current = sections.find((s) => s.id === activeId) ?? sections[0];

  const place = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.min(720, window.innerWidth - 16);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    const top = r.bottom + 6;
    setPos({ top, left, width, height: Math.max(300, Math.min(540, window.innerHeight - top - 12)) });
  };

  useLayoutEffect(() => {
    if (open && !phone) place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phone]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      // A dropdown opened from inside the panel (ChipSelect, SkuFilterChip)
      // lives in its own portal: a click in it is not "outside".
      if ((t as Element).closest?.('[role="listbox"], [data-floating-menu]')) return;
      onOpenChange(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      onOpenChange(false);
      triggerRef.current?.focus();
    };
    const onMove = () => !phone && place();
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    // A sheet on a phone: keep the page from scrolling behind it.
    const prevOverflow = document.body.style.overflow;
    if (phone) document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phone]);

  const panel = current && (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={title}
      className={
        phone
          ? // A sheet over the top three quarters of the visible screen (dvh, not
            // vh, so it measures what is actually on screen), with its buttons
            // along its bottom edge in easy reach.
            'fixed inset-x-0 top-0 z-50 flex h-[75dvh] flex-col overflow-hidden rounded-b-2xl bg-white shadow-2xl'
          : 'fixed z-50 flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl'
      }
      style={
        phone
          ? undefined
          : pos
            ? { top: pos.top, left: pos.left, width: pos.width, height: pos.height }
            : { visibility: 'hidden' }
      }
    >
      <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3">
        <h2 className="text-base font-bold text-slate-900">{title}</h2>
        <div className="flex items-center gap-1.5">
          {phone && headerAction}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close filters"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <nav
          aria-label="Filter by"
          className={`shrink-0 overflow-y-auto border-r border-slate-200 bg-slate-50 ${phone ? 'w-[42%] max-w-[190px]' : 'w-56'}`}
        >
          {sections.map((s) => {
            const on = s.id === current.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveId(s.id)}
                aria-current={on || undefined}
                className={`relative block w-full border-b border-slate-100 px-4 py-3 text-left transition-colors ${
                  on ? 'bg-white' : 'hover:bg-slate-100'
                }`}
              >
                {on && <span className="absolute inset-y-0 left-0 w-1 bg-indigo-600" />}
                <span className={`block text-sm ${on ? 'font-bold text-slate-900' : 'font-semibold text-slate-600'}`}>
                  {s.label}
                </span>
                {s.summary && <span className="mt-0.5 block truncate text-xs font-semibold text-indigo-600">{s.summary}</span>}
              </button>
            );
          })}
        </nav>

        {/* Keyed by section: a list's search box starts empty on each one. */}
        <div key={current.id} className="min-w-0 flex-1 overflow-y-auto p-4">
          <h3 className="text-sm font-bold text-slate-900">{current.label}</h3>
          {current.hint && <p className="mt-1 text-xs leading-relaxed text-slate-500">{current.hint}</p>}
          <div className="mt-3">{current.content}</div>
        </div>
      </div>

      <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-3">
        {footer}
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={activeCount ? `${title} (${activeCount} applied)` : title}
        title={title}
        className={`relative flex h-9 shrink-0 items-center gap-2 rounded-lg border text-xs font-semibold transition-colors ${
          phone ? 'w-9 justify-center' : 'px-3'
        } ${
          open || activeCount > 0
            ? 'border-indigo-300 bg-indigo-50/70 text-indigo-700'
            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
        }`}
      >
        <SlidersHorizontal size={15} className={activeCount > 0 || open ? 'text-indigo-500' : 'text-slate-400'} />
        {!phone && <span>{title}</span>}
        {activeCount > 0 && (
          <span
            className={`flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-indigo-600 px-1 text-[10px] font-bold leading-none text-white ${
              phone ? 'absolute -right-1.5 -top-1.5 ring-2 ring-white' : ''
            }`}
          >
            {activeCount}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <>
            {/* The page shows dimmed below the sheet; a tap there closes it. */}
            {phone && <div className="fixed inset-0 z-40 bg-slate-900/40" aria-hidden />}
            {panel}
          </>,
          document.body,
        )}
    </>
  );
};

export default FilterPanel;
